import axios from 'axios';
import { setupUserBlockInterceptor } from '@shared/utils/setupUserBlockInterceptor';
import { buildAntiBotMeta } from '@shared/utils/antiBot';
import { detectDeviceType } from '@shared/utils/device';
import type {
  Event,
  EventDetails,
  PurchaseRequest,
  PurchaseResponse,
  WaitlistResponse,
  EventRequestTokenPayload,
  RequestTokenResponse,
  ConfirmPointsRequest,
  ConfirmPointsResponse,
  LeaderboardEntry,
} from './events.types';

const eventsApi = axios.create({
  baseURL: import.meta.env.VITE_API_EVENTS_URL as string,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

setupUserBlockInterceptor(eventsApi);

export const eventsService = {
  async getEvents(signal?: AbortSignal) {
    return await eventsApi.get<Event[]>('/events', { signal });
  },
  async getMyEvents(signal?: AbortSignal) {
    return await eventsApi.get<Event[]>('/events/my-events', { signal });
  },
  async getEventById(id: string, signal?: AbortSignal) {
    return await eventsApi.get<EventDetails>(`/events/${id}`, { signal });
  },
  async purchaseEvent(id: string, data?: PurchaseRequest) {
    return await eventsApi.post<PurchaseResponse>(
      `/events/${id}/purchase`,
      data,
    );
  },
  async joinWaitlist(id: string) {
    return await eventsApi.post<WaitlistResponse>(`/events/${id}/waitlist`);
  },
  async getEventLeaderboard(id: string, signal?: AbortSignal) {
    return await eventsApi.get<LeaderboardEntry[]>(
      `/events/${id}/leaderboard`,
      {
        signal,
      },
    );
  },
  async requestToken(data: EventRequestTokenPayload) {
    return await eventsApi.post<RequestTokenResponse>(
      '/points/request-token',
      data,
    );
  },
  async confirmPoints(payload: ConfirmPointsRequest, token: string) {
    const device_type = detectDeviceType();
    const meta = buildAntiBotMeta();
    const anti_bot_metrics = {
      uaInfo: meta.uaInfo,
      screen: meta.screen,
      system: meta.system,
      network: meta.network,
      signals: meta.signals,
    };
    const anti_bot_malicious = {
      keywords: meta.keywords,
      flags: meta.flags,
      riskScore: meta.riskScore,
      riskLevel: meta.riskLevel,
      reasons: meta.reasons,
    };

    const confirmPayload = {
      ...payload,
      device_type: { type: device_type },
      anti_bot_metrics,
      anti_bot_malicious,
    };

    return await eventsApi.post<ConfirmPointsResponse>(
      '/points/request-token/confirm',
      confirmPayload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
  },
  async proxyStartEventSession(payload: Record<string, unknown>) {
    return await eventsApi.post<Record<string, unknown>>(
      '/session/start',
      payload,
    );
  },
  async proxyEventSessionHeartbeat(payload: Record<string, unknown>) {
    return await eventsApi.post<Record<string, unknown>>(
      '/session/heartbeat',
      payload,
    );
  },
};
